import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { User, UserRole } from '../../types/auth';
import { createUser, updateUser, resetUserPassword } from '../../services/userService';

interface UserFormData {
  email: string;
  password?: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  profile?: {
    school?: string;
    grade?: number;
    parentEmail?: string;
    phone?: string;
  };
}

interface UserFormDialogProps {
  open: boolean;
  onClose: () => void;
  user: User | null;
  isEditMode: boolean;
  onSuccess: () => void;
}

// Separate component for grade field to properly use hooks
const GradeField: React.FC<{
  value?: number;
  onChange: (value: number | undefined) => void;
  field: {
    name: string;
    onBlur: () => void;
    ref: React.Ref<HTMLInputElement>;
  };
}> = ({ value, onChange, field }) => {
  const [displayValue, setDisplayValue] = React.useState(value?.toString() ?? '');

  React.useEffect(() => {
    setDisplayValue(value?.toString() ?? '');
  }, [value]);

  return (
    <TextField
      {...field}
      value={displayValue}
      onChange={(e) => {
        const val = e.target.value;
        setDisplayValue(val);

        // Allow empty string for clearing
        if (val === '') {
          onChange(undefined);
        } else {
          // Parse and validate
          const numVal = parseInt(val, 10);
          if (!isNaN(numVal)) {
            onChange(numVal);
          }
        }
      }}
      fullWidth
      label="Grade Level"
      type="text"
      inputProps={{
        inputMode: 'numeric',
        pattern: '[5-8]'
      }}
      margin="dense"
      helperText="Grades 5-8"
    />
  );
};

const UserFormDialog: React.FC<UserFormDialogProps> = ({
  open,
  onClose,
  user,
  isEditMode,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  // Initialize form with user data if in edit mode
  const defaultValues: UserFormData = {
    email: user?.email || '',
    password: '',
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    role: user?.role || 'student',
    profile: {
      school: user?.profile?.school || '',
      grade: user?.profile?.grade || undefined,
      parentEmail: user?.profile?.parentEmail || '',
      phone: user?.profile?.phone || ''
    }
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<UserFormData>({
    defaultValues
  });

  // Reset form when user changes
  React.useEffect(() => {
    if (open) {
      const formValues: UserFormData = {
        email: user?.email || '',
        password: '',
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        role: user?.role || 'student',
        profile: {
          school: user?.profile?.school || '',
          grade: user?.profile?.grade || undefined,
          parentEmail: user?.profile?.parentEmail || '',
          phone: user?.profile?.phone || ''
        }
      };
      reset(formValues);
    }
  }, [open, user, reset]);

  // Handle form submission
  const onSubmit = async (data: UserFormData) => {
    setLoading(true);
    setError(null);

    console.log('[UserFormDialog] Form data received:', data);

    try {
      if (isEditMode && user) {
        // Update user profile data (excluding password)
        const { password: _password, ...updateData } = data;

        console.log('[UserFormDialog] Update data before profile transformation:', updateData);

        // Convert empty strings to undefined for profile fields
        if (updateData.profile) {
          updateData.profile = {
            school: updateData.profile.school || undefined,
            grade: updateData.profile.grade || undefined,
            parentEmail: updateData.profile.parentEmail || undefined,
            phone: updateData.profile.phone || undefined,
          };
        }

        console.log('[UserFormDialog] Update data being sent to API:', updateData);

        await updateUser(user.id, updateData);

        // If password is provided, reset it separately
        if (data.password && data.password.trim() !== '') {
          await resetUserPassword(user.id, data.password);
        }
      } else {
        // For create, password is required
        if (!data.password) {
          setError('Password is required');
          setLoading(false);
          return;
        }

        // Convert empty strings to undefined for profile fields
        const createData = { ...data };
        if (createData.profile) {
          createData.profile = {
            school: createData.profile.school || undefined,
            grade: createData.profile.grade || undefined,
            parentEmail: createData.profile.parentEmail || undefined,
            phone: createData.profile.phone || undefined,
          };
        }

        // @ts-ignore - TypeScript complains about password being possibly undefined
        await createUser(createData);
      }

      onSuccess();
    } catch (err) {
      setError('Failed to save user. Please try again.');
      console.error('Error saving user:', err);
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {isEditMode ? 'Edit User' : 'Create New User'}
      </DialogTitle>
      
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Account Information
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Controller
                name="email"
                control={control}
                rules={{
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address'
                  }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Email Address"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    margin="dense"
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Controller
                name="password"
                control={control}
                rules={{
                  minLength: {
                    value: 6,
                    message: 'Password must be at least 6 characters'
                  },
                  ...(isEditMode 
                    ? {} 
                    : { required: 'Password is required for new users' })
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={isEditMode ? "New Password (leave blank to keep current)" : "Password"}
                    type={showPassword ? 'text' : 'password'}
                    error={!!errors.password}
                    helperText={errors.password?.message}
                    margin="dense"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={togglePasswordVisibility}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Personal Information
            </Typography>
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Controller
                name="firstName"
                control={control}
                rules={{
                  required: 'First name is required',
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="First Name"
                    error={!!errors.firstName}
                    helperText={errors.firstName?.message}
                    margin="dense"
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Controller
                name="lastName"
                control={control}
                rules={{
                  required: 'Last name is required',
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Last Name"
                    error={!!errors.lastName}
                    helperText={errors.lastName?.message}
                    margin="dense"
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Controller
                name="role"
                control={control}
                rules={{
                  required: 'Role is required',
                }}
                render={({ field }) => (
                  <FormControl fullWidth margin="dense">
                    <InputLabel id="role-label">Role</InputLabel>
                    <Select
                      {...field}
                      labelId="role-label"
                      label="Role"
                      error={!!errors.role}
                    >
                      <MenuItem value="student">Student</MenuItem>
                      <MenuItem value="admin">Administrator</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
          </Grid>
          
          <Divider sx={{ my: 2 }} />
          
          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Student Information (Optional)
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Additional information for student accounts.
            </Typography>
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Controller
                name="profile.school"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="School Name"
                    margin="dense"
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Controller
                name="profile.grade"
                control={control}
                render={({ field: { value, onChange, ...field } }) => (
                  <GradeField
                    value={value}
                    onChange={onChange}
                    field={field}
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Controller
                name="profile.parentEmail"
                control={control}
                rules={{
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address'
                  }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Parent Email"
                    error={!!errors.profile?.parentEmail}
                    helperText={errors.profile?.parentEmail?.message}
                    margin="dense"
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Controller
                name="profile.phone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Phone Number"
                    margin="dense"
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={onClose}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : isEditMode ? 'Save Changes' : 'Create User'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UserFormDialog;